import { CommercialPerformerTypes, CommercialType, RoleFilter, RolePerformerType, RoleType } from '@shared/types';

type TRoleLike = { performer_type?: string | null };

/**
 * Whether a session activity's roles must be cleared because the session's commercial type
 * changed to one those roles can't perform in (e.g. TV → Radio drops on-camera roles).
 *
 * Only a real change counts: `previousType` is undefined the first time an activity row renders,
 * and treating that as a change used to save (or wipe the roles of) every activity just from
 * opening the page - each save then recalculating the whole estimate, a dozen identical saves at once.
 */
export const shouldClearRolesForCommercialType = (
  previousType: string | undefined,
  nextType: string | undefined,
  roles: TRoleLike[] | undefined
): boolean => {
  if (previousType === undefined || !nextType || previousType === nextType) return false;
  if (!roles?.length) return false;

  const allowed = CommercialPerformerTypes[nextType] ?? [];
  return roles.some((role) => !allowed.includes(role.performer_type as (typeof allowed)[number]));
};

/**
 * The performer types a commercial type's roles can be: a radio spot's radio performers, radio groups and AFM
 * musicians; a TV, digital or social spot's TV performers and AFM musicians. Undefined (any) for other types, and
 * before a commercial type is chosen.
 */
export const getCommercialPerformerTypes = (commercialType?: string): RolePerformerType[] | undefined =>
  commercialType === CommercialType.Radio
    ? CommercialPerformerTypes[CommercialType.Radio]
    : commercialType === CommercialType.TV ||
        commercialType === CommercialType.Digital ||
        commercialType === CommercialType.Social
      ? CommercialPerformerTypes[CommercialType.TV]
      : undefined;

/**
 * The roles a session or a usage of a commercial type can have, by performer type (a role type like Actor or
 * Singer is used by both contracts). Every role, before a commercial type is chosen.
 */
export const getCommercialRoles = <T extends TRoleLike>(commercialType: string | undefined, roles: T[]): T[] => {
  const performerTypes = getCommercialPerformerTypes(commercialType);
  return performerTypes
    ? roles.filter((role) => performerTypes.includes(role.performer_type as RolePerformerType))
    : roles;
};

/**
 * The performer types a project's media allow together, for the project wizard. Undefined (any) without media, or
 * with a medium no contract covers yet.
 */
export const getMediaPerformerTypes = (media: string[]): RolePerformerType[] | undefined => {
  const perMedium = media.map(getCommercialPerformerTypes);
  if (!perMedium.length || perMedium.some((types) => !types)) return undefined;
  return [...new Set(perMedium.flatMap((types) => types ?? []))];
};

/** A role type the performer type allows: the one given, or else the performer type's first */
export const getRoleTypeFor = (performerType: RolePerformerType, roleType: RoleType): RoleType => {
  const allowed = RoleFilter[performerType] as RoleType[] | undefined;
  return !allowed?.length || allowed.includes(roleType) ? roleType : allowed[0];
};
